import { useEffect, useRef } from "react";

interface PhysicsItem {
  el: HTMLElement;
  originalStyle: {
    position: string;
    left: string;
    top: string;
    width: string;
    transform: string;
    transition: string;
    zIndex: string;
    pointerEvents: string;
  };
  x: number;
  y: number;
  vy: number;
  vx: number;
  settled: boolean;
}

interface GravityModeProps {
  containerRef: React.RefObject<HTMLElement | null>;
  onEnd: () => void;
}

export default function GravityMode({ containerRef, onEnd }: GravityModeProps) {
  const itemsRef = useRef<PhysicsItem[]>([]);
  const rafRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Strategy: collect "block-level" elements at consistent granularity.
    //
    // We want whole visual blocks to fall, not thousands of tiny text nodes.
    // Priority order — pick the most-specific visible block and skip its children:
    //
    //  1. MuiCard-root / MuiPaper-root — the calendar card, quick-link cards
    //  2. MuiToolbar-root              — the header toolbar
    //  3. MuiChip-root                 — chips (calendar events, role chips)
    //  4. MuiAvatar-root               — link logo avatars
    //  5. button / .MuiButtonBase-root — standalone buttons
    //  6. h1–h6 / .MuiTypography-h*   — headings not inside a card/paper

    const BLOCK_SELECTORS =
      ".MuiCard-root, .MuiPaper-root, .MuiToolbar-root";
    const FINE_SELECTORS =
      ".MuiChip-root, .MuiAvatar-root, button, .MuiButtonBase-root, " +
      "h1, h2, h3, h4, h5, h6, " +
      ".MuiTypography-h1, .MuiTypography-h2, .MuiTypography-h3, " +
      ".MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6";

    const blockEls = Array.from(
      document.querySelectorAll<HTMLElement>(BLOCK_SELECTORS),
    );

    // Build a set of all descendants of blocks so we can exclude them
    const insideBlock = new Set<HTMLElement>();
    blockEls.forEach((b) => {
      b.querySelectorAll<HTMLElement>("*").forEach((child) =>
        insideBlock.add(child),
      );
    });

    // Fine-grained elements that are NOT inside any block
    const fineEls = Array.from(
      document.querySelectorAll<HTMLElement>(FINE_SELECTORS),
    ).filter((el) => !insideBlock.has(el));

    // Combine and filter to only on-screen visible elements
    const allEls = [...blockEls, ...fineEls].filter((el) => {
      const r = el.getBoundingClientRect();
      return (
        r.width > 0 &&
        r.height > 0 &&
        r.top < window.innerHeight &&
        r.bottom > 0 &&
        r.left < window.innerWidth &&
        r.right > 0
      );
    });

    const floorY = window.innerHeight;

    itemsRef.current = allEls.map((el) => {
      const rect = el.getBoundingClientRect();
      const originalStyle = {
        position: el.style.position,
        left: el.style.left,
        top: el.style.top,
        width: el.style.width,
        transform: el.style.transform,
        transition: el.style.transition,
        zIndex: el.style.zIndex,
        pointerEvents: el.style.pointerEvents,
      };

      // Detach from normal flow and pin to current visual position
      el.style.position = "fixed";
      el.style.left = `${rect.left}px`;
      el.style.top = `${rect.top}px`;
      el.style.width = `${rect.width}px`;
      el.style.transform = "";
      el.style.transition = "none";
      el.style.zIndex = "9998";
      el.style.pointerEvents = "none";

      return {
        el,
        originalStyle,
        x: rect.left,
        y: rect.top,
        vy: -(Math.random() * 3 + 0.5), // small upward pop
        vx: (Math.random() - 0.5) * 3,
        settled: false,
      };
    });

    const GRAVITY = 0.55;
    const BOUNCE = 0.28;

    const tick = () => {
      let allSettled = true;

      itemsRef.current.forEach((item) => {
        if (item.settled) return;

        item.vy += GRAVITY;
        item.y += item.vy;
        item.x += item.vx;

        // Use cached height to avoid layout thrash (getBoundingClientRect on
        // a fixed element is cheap but still — read once is better)
        const elH = item.el.offsetHeight || 20;
        const bottom = item.y + elH;

        if (bottom >= floorY) {
          item.y = floorY - elH;
          item.vy *= -BOUNCE;
          item.vx *= 0.75;

          if (Math.abs(item.vy) < 0.5 && Math.abs(item.vx) < 0.5) {
            item.settled = true;
            item.vy = 0;
            item.vx = 0;
          }
        }

        item.el.style.top = `${item.y}px`;
        item.el.style.left = `${item.x}px`;

        if (!item.settled) allSettled = false;
      });

      if (!allSettled) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    timerRef.current = setTimeout(() => {
      cleanup();
      onEnd();
    }, 10000);

    return cleanup;
  }, []);

  function cleanup() {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);
    itemsRef.current.forEach(({ el, originalStyle }) => {
      el.style.position = originalStyle.position;
      el.style.left = originalStyle.left;
      el.style.top = originalStyle.top;
      el.style.width = originalStyle.width;
      el.style.transform = originalStyle.transform;
      el.style.transition = originalStyle.transition;
      el.style.zIndex = originalStyle.zIndex;
      el.style.pointerEvents = originalStyle.pointerEvents;
    });
    itemsRef.current = [];
  }

  return null;
}
